import { useEffect } from 'react';

import { useAtom, useSetAtom } from 'jotai';
import { AnimatePresence, motion, type MotionProps } from 'motion/react';
import { useTranslation } from 'react-i18next';

import useExploreSearch from '@/hooks/useExploreSearch';

import { cn } from '@/utils/tailwind';

import { AnswersChip, Feedback, SmartResponse, TryItOut } from './components';
import SourcePreview from './components/SourcePreview';
import { getStableFunnyNoAnswerResponse } from './data';
import { getSmartSearchDisplay } from './getSmartSearchDisplay';
import { previewingSourceAtom, searchFeedbackContextAtom } from './state';

const opacityTransition: MotionProps = {
  initial: { opacity: 1 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.3 },
};

interface AISearchPOCProps {
  searchValue: string;
}

const AISearchPOC: React.FC<AISearchPOCProps> = ({ searchValue }) => {
  const { t } = useTranslation();
  const { data, isFetching } = useExploreSearch(searchValue);
  const response = data;
  const smart = response?.smart;

  const [previewingSource, setPreviewingSource] = useAtom(previewingSourceAtom);
  const setSearchFeedbackContext = useSetAtom(searchFeedbackContextAtom);

  const display = getSmartSearchDisplay(smart);

  const noAnswerSeed =
    smart?.queryId ??
    [JSON.stringify(smart?.error ?? null), smart?.answer ?? ''].join(':');

  const noAnswerText = getStableFunnyNoAnswerResponse(noAnswerSeed);

  useEffect(() => {
    setPreviewingSource(undefined);
  }, [searchValue, setPreviewingSource]);

  useEffect(() => {
    if (response?.smart?.queryId) {
      setSearchFeedbackContext({ queryId: response.smart.queryId });
    } else {
      setSearchFeedbackContext(undefined);
    }
  }, [response?.smart?.queryId, setSearchFeedbackContext]);

  return (
    <div className="mt-3">
      <div
        className={cn(
          'rounded-md border-0 transition-[background-color,padding-block,padding-inline] duration-400',
          {
            'border-secondary-base bg-th-bg flex min-h-[36px] flex-col gap-3 rounded-md border px-5 py-6':
              searchValue.split(' ').length > 2,
          },
        )}
      >
        {searchValue.split(' ').length > 2 ? (
          <div
            className={cn('grid grid-cols-1', {
              'md:grid-cols-[1fr_auto_1fr]': previewingSource,
            })}
          >
            <div>
              <div className="flex justify-between gap-2">
                <AnswersChip t={t} />
                {smart &&
                  (display.kind === 'answer' ||
                    (display.kind === 'noAnswer' && !smart.answer)) && (
                    <Feedback smartAnswer={smart} question={searchValue} />
                  )}
              </div>
              <div className="min-h-sticky-menu-top mt-2 max-w-[720px]">
                <AnimatePresence initial={false} mode="wait">
                  {isFetching ? (
                    <motion.span
                      key="thinking"
                      className="animate-text-shimmering"
                      {...opacityTransition}
                    >
                      {t('AISearchPOC.thinking')}...
                    </motion.span>
                  ) : display.kind === 'questionNotSupported' ? (
                    <motion.div
                      key="question-not-supported"
                      {...opacityTransition}
                    >
                      <SmartResponse
                        results={t('AISearchPOC.questionNotSupported')}
                      />
                    </motion.div>
                  ) : display.kind === 'answer' ? (
                    <motion.div key="results" {...opacityTransition}>
                      <SmartResponse
                        results={display.text}
                        sources={display.sources}
                      />
                    </motion.div>
                  ) : (
                    <motion.div key="no-answer" {...opacityTransition}>
                      <SmartResponse results={noAnswerText} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            {previewingSource && (
              <>
                <div className="bg-divider max-md:my-5 max-md:h-px max-md:w-full md:mx-5 md:h-full md:w-px" />
                <SourcePreview
                  source={previewingSource.source}
                  matchedText={previewingSource.text}
                />
              </>
            )}
          </div>
        ) : (
          <TryItOut />
        )}
      </div>
    </div>
  );
};

export default AISearchPOC;
